import { TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { GoalTabTriggers } from "@/components/flow/goal-tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useT } from "@/lib/i18n/use-t";
import type { FlowPage } from "@/lib/types/flow";
import type { GoalListItem } from "@/lib/types/goal";
import { PublicGoalContext } from "./public-goal-context";
import { T } from "./public-goal-nav.i18n";

type PublicGoalNavProps = {
  goals: GoalListItem[];
  goalPages: FlowPage[];
  portfolioPages: FlowPage[];
  /** QL-120 — goal-page visibility for the selected goal. */
  isGoalPageVisible?: (page: FlowPage, goalId: string) => boolean;
  /** QL-120 — portfolio-page visibility for the active (goal × portfolio) pair. */
  isPortfolioPageVisible?: (page: FlowPage, goalId: string, portfolioId: string) => boolean;
  globalPagesBefore: FlowPage[];
  globalPagesAfter: FlowPage[];
  multiPortfolio: boolean;
  isCompleted: boolean;
  /** QL-593 — false once the instance holds the flow's maximum number of goals. */
  canAddGoal: boolean;
  onAddGoal: () => void;
  onDeleteGoal: (goalId: string) => void;
  currentGoalId: string | null;
  onCurrentGoalChange: (goalId: string | null) => void;
  renderGoalPage: (page: FlowPage, goalId: string) => React.ReactNode;
  renderPortfolioPage: (page: FlowPage, goalId: string, portfolio: any) => React.ReactNode;
  renderGlobalPage: (page: FlowPage) => React.ReactNode;
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
};

/**
 * Public multi-goal L1 navigation (QP-150). Mirror of {@link AdvisorGoalNav} using
 * {@link PublicGoalContext} as the per-goal data wrapper (public portfolio hooks).
 *
 * The top row: global-before tabs, goal tabs (with delete), global-after tabs, and "Add Goal".
 * Selecting a goal renders its per-goal panel via PublicGoalContext → GoalContextView.
 */
export function PublicGoalNav({
  goals,
  goalPages,
  portfolioPages,
  isGoalPageVisible,
  isPortfolioPageVisible,
  globalPagesBefore,
  globalPagesAfter,
  multiPortfolio,
  isCompleted,
  canAddGoal,
  onAddGoal,
  onDeleteGoal,
  currentGoalId,
  onCurrentGoalChange,
  renderGoalPage,
  renderPortfolioPage,
  renderGlobalPage,
  showValidationErrors,
  markers,
}: PublicGoalNavProps) {
  const t = useT(T);

  const [selectedGlobalPageId, setSelectedGlobalPageId] = useState<string | null>(
    globalPagesBefore.length > 0 ? `before:${globalPagesBefore[0].id}` : null,
  );

  const handleTabChange = (value: string) => {
    const [type, id] = value.split(":");
    if (type === "goal") {
      onCurrentGoalChange(id);
    } else if (type === "before" || type === "after") {
      onCurrentGoalChange(null);
      setSelectedGlobalPageId(value);
    }
  };

  const currentTabValue = useMemo(() => {
    if (currentGoalId && goals.some((g) => g.id === currentGoalId)) {
      return `goal:${currentGoalId}`;
    }
    if (selectedGlobalPageId) return selectedGlobalPageId;
    if (globalPagesBefore.length > 0) return `before:${globalPagesBefore[0].id}`;
    if (globalPagesAfter.length > 0) return `after:${globalPagesAfter[0].id}`;
    return "";
  }, [currentGoalId, goals, globalPagesBefore, globalPagesAfter, selectedGlobalPageId]);

  const selectedGoal = currentGoalId ? goals.find((g) => g.id === currentGoalId) : null;

  const selectedGlobalPage = useMemo(() => {
    if (!selectedGlobalPageId || currentGoalId) return null;
    const [type, id] = selectedGlobalPageId.split(":");
    if (type === "before") return globalPagesBefore.find((p) => p.id === id) || null;
    if (type === "after") return globalPagesAfter.find((p) => p.id === id) || null;
    return null;
  }, [selectedGlobalPageId, currentGoalId, globalPagesBefore, globalPagesAfter]);

  if (goals.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <h3 className="text-lg font-semibold mb-2">{t.noGoalsYet}</h3>
          <p className="text-muted-foreground text-center mb-4 max-w-sm">
            {t.createGoalsDescription}
          </p>
          <Button onClick={onAddGoal}>{t.addFirstGoal}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* L1 tab bar: globals-before + goal tabs + globals-after; Add Goal outside the box. */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex-1">
          <Tabs value={currentTabValue} onValueChange={handleTabChange}>
            <TabsList className="flex w-fit max-w-full justify-start gap-1">
              {globalPagesBefore.map((page) => (
                <TabsTrigger
                  key={page.id}
                  value={`before:${page.id}`}
                  className="shrink-0 flex items-center justify-center gap-1.5"
                >
                  {page.name}
                  {showValidationErrors && markers.missingPageIds.has(page.id) && (
                    <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                  )}
                </TabsTrigger>
              ))}

              <GoalTabTriggers
                goals={goals}
                currentGoalId={currentGoalId}
                onSelectGoal={onCurrentGoalChange}
                onDeleteGoal={onDeleteGoal}
                canDelete={!isCompleted && goals.length > 1}
                missingGoalIds={markers.missingGoalIds}
                showValidationErrors={showValidationErrors}
                deleteLabel={t.delete}
              />

              {globalPagesAfter.map((page) => (
                <TabsTrigger
                  key={page.id}
                  value={`after:${page.id}`}
                  className="shrink-0 flex items-center justify-center gap-1.5"
                >
                  {page.name}
                  {showValidationErrors && markers.missingPageIds.has(page.id) && (
                    <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        {!isCompleted && canAddGoal && (
          <Button onClick={onAddGoal} size="sm">
            {t.addGoal}
          </Button>
        )}
      </div>

      {/* Content: a global page, or the selected goal's context panel. */}
      {!currentGoalId && selectedGlobalPage && <div>{renderGlobalPage(selectedGlobalPage)}</div>}

      {currentGoalId && selectedGoal && (
        <PublicGoalContext
          goalId={currentGoalId}
          isCompleted={isCompleted}
          multiPortfolio={multiPortfolio}
          goalPages={goalPages}
          portfolioPages={portfolioPages}
          isGoalPageVisible={isGoalPageVisible}
          isPortfolioPageVisible={isPortfolioPageVisible}
          renderGoalPage={renderGoalPage}
          renderPortfolioPage={renderPortfolioPage}
          showValidationErrors={showValidationErrors}
          markers={markers}
        />
      )}
    </div>
  );
}
